// ============================================================================
// Funcionalidades:
// - Selecionar condição para um dente específico
// - Pesquisar condições por código ou descrição
// - Visualização em grid responsivo
// - Design consistente com os demais diálogos
// ============================================================================

import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FaTag, FaTooth, FaTrashAlt, FaTimes, FaSearch } from "react-icons/fa";

// Lista de condições odontológicas
const CONDICOES = [
  { codigo: "A", descricao: "Ausente", categoria: "presenca" },
  { codigo: "E", descricao: "Extraído", categoria: "presenca" },
  { codigo: "H", descricao: "Hígido", categoria: "saude" },
  { codigo: "Hs", descricao: "Hígido selado", categoria: "saude" },
  { codigo: "C", descricao: "Cariado", categoria: "lesao" },
  { codigo: "Rc", descricao: "Restaurado com cárie", categoria: "restauracao" },
  { codigo: "Fr", descricao: "Fratura", categoria: "lesao" },
  { codigo: "R", descricao: "Restaurado", categoria: "restauracao" },
  { codigo: "Co", descricao: "Coroa", categoria: "protese" },
  { codigo: "P", descricao: "Pulpite", categoria: "endodontia" },
  { codigo: "Np", descricao: "Necrose pulpar", categoria: "endodontia" },
  { codigo: "Ab", descricao: "Abscesso", categoria: "endodontia" },
  { codigo: "Fi", descricao: "Fístula", categoria: "endodontia" },
  { codigo: "Re", descricao: "Reabsorção", categoria: "lesao" },
  { codigo: "M", descricao: "Mobilidade", categoria: "periodontal" },
  { codigo: "S", descricao: "Sensível", categoria: "sintoma" },
  {
    codigo: "LCNC",
    descricao: "Lesão cervical não cariosa",
    categoria: "lesao",
  },
  { codigo: "D", descricao: "Desgaste", categoria: "lesao" },
  { codigo: "Tr", descricao: "Trinca", categoria: "lesao" },
  { codigo: "Fd", descricao: "Faceta de desgaste", categoria: "restauracao" },
  { codigo: "Mb", descricao: "Mancha branca", categoria: "lesao" },
  { codigo: "Hp", descricao: "Hipoplasia", categoria: "desenvolvimento" },
  {
    codigo: "Endo",
    descricao: "Tratamento endodôntico",
    categoria: "endodontia",
  },
  {
    codigo: "EndoP",
    descricao: "Endodôntico em andamento",
    categoria: "endodontia",
  },
  {
    codigo: "Rp",
    descricao: "Restauração provisória",
    categoria: "restauracao",
  },
  { codigo: "Pr", descricao: "Prótese", categoria: "protese" },
  { codigo: "Imp", descricao: "Implante", categoria: "protese" },
  { codigo: "I", descricao: "Incluso", categoria: "posicao" },
  { codigo: "SI", descricao: "Semi-incluso", categoria: "posicao" },
  { codigo: "Sn", descricao: "Supranumerário", categoria: "posicao" },
  { codigo: "Mp", descricao: "Mal posicionado", categoria: "posicao" },
  { codigo: "Gv", descricao: "Girovertido", categoria: "posicao" },
  { codigo: "Gg", descricao: "Gengivite", categoria: "periodontal" },
  { codigo: "Per", descricao: "Periodontite", categoria: "periodontal" },
  { codigo: "Bp", descricao: "Bolsa periodontal", categoria: "periodontal" },
];

const CondicaoSelector = ({
  visible,
  onHide,
  onSelect,
  onClear,
  denteNumero,
  condicaoAtual,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [condicaoSelecionada, setCondicaoSelecionada] = useState(
    condicaoAtual || null,
  );

  // ==========================================================================
  // DETECÇÃO DE DISPOSITIVO MÓVEL
  // ==========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ==========================================================================
  // ATUALIZAR CONDIÇÃO SELECIONADA QUANDO PROPS MUDAR
  // ==========================================================================
  useEffect(() => {
    setCondicaoSelecionada(condicaoAtual || null);
  }, [condicaoAtual]);

  // ==========================================================================
  // FILTRAR CONDIÇÕES BASEADO NA BUSCA
  // ==========================================================================
  const getCondicoesFiltradas = () => {
    let filtradas = CONDICOES;

    if (searchTerm.trim()) {
      filtradas = filtradas.filter(
        (cond) =>
          cond.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cond.descricao.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return filtradas;
  };

  // ==========================================================================
  // SELECIONAR CONDIÇÃO
  // ==========================================================================
  const handleSelect = (condicao) => {
    setCondicaoSelecionada(condicao);
    if (onSelect) {
      onSelect(condicao);
    }
    handleClose();
  };

  // ==========================================================================
  // REMOVER CONDIÇÃO (DESMARCAR)
  // ==========================================================================
  const handleClear = () => {
    setCondicaoSelecionada(null);
    if (onClear) {
      onClear();
    }
    handleClose();
  };

  // ==========================================================================
  // FECHAR DIÁLOGO
  // ==========================================================================
  const handleClose = () => {
    setSearchTerm("");
    onHide();
  };

  // ==========================================================================
  // LIMPAR PESQUISA
  // ==========================================================================
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const condicoesFiltradas = getCondicoesFiltradas();

  // ==========================================================================
  // DETERMINAR NÚMERO DE COLUNAS BASEADO NO TAMANHO DA TELA
  // ==========================================================================
  const getGridColumns = () => {
    if (isMobile) {
      return "repeat(2, 1fr)"; // 2 colunas no celular
    }
    return "repeat(4, 1fr)"; // 4 colunas no desktop
  };

  // ==========================================================================
  // ESTILOS COMPARTILHADOS
  // ==========================================================================
  const inputStyle = {
    width: "100%",
    height: "40px",
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: "0.9rem",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "0 35px 0 12px",
    transition: "all 0.2s",
    outline: "none",
  };

  const iconStyle = {
    color: "#B83732",
    fontSize: "1rem",
    minWidth: "20px",
  };

  // ==========================================================================
  // RENDER PRINCIPAL
  // ==========================================================================
  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{
        width: "90vw",
        maxWidth: "768px",
        fontFamily: "'Rajdhani', sans-serif",
      }}
      contentStyle={{
        overflowY: "auto",
        overflowX: "hidden",
        maxHeight: "70vh",
      }}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "-25px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div style={{ marginRight: "12px", paddingLeft: "8px" }}>
              <FaTooth style={{ fontSize: "2.4rem", color: "#B83732" }} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  width: "100%",
                  letterSpacing: "1px",
                  color: "#718096",
                }}
              >
                ADICIONAR AO
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  marginTop: "-8px",
                  color: "#2d3748",
                }}
              >
                DENTE {denteNumero}
              </div>
            </div>
          </div>
        </div>
      }
      draggable={false}
    >
      <div
        style={{
          backgroundColor: "#FAFAFA",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        }}
      >
        {/* CONDIÇÃO ATUAL - TAG INFORMATIVA */}
        {condicaoSelecionada && (
          <div
            style={{
              backgroundColor: "#fff0f0",
              border: "1px solid #B83732",
              borderRadius: "8px",
              padding: "10px 15px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FaTooth style={{ color: "#B83732", fontSize: "1.2rem" }} />
              <div>
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#B83732",
                    fontSize: "1rem",
                  }}
                >
                  {condicaoSelecionada.codigo}
                </span>
                <span
                  style={{
                    color: "#4a5568",
                    fontSize: "0.85rem",
                    marginLeft: "8px",
                  }}
                >
                  {condicaoSelecionada.descricao}
                </span>
              </div>
            </div>
            <Button
              icon="pi pi-trash"
              label="REMOVER"
              onClick={handleClear}
              className="p-button-danger p-button-sm"
              style={{
                padding: "6px 12px",
                fontSize: "0.75rem",
                fontWeight: "500",
              }}
            />
          </div>
        )}

        {/* CAMPO DE PESQUISA */}
        <div style={{ marginBottom: "20px", position: "relative" }}>
          <div style={{ position: "relative" }}>
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar por código ou descrição..."
              style={{
                ...inputStyle,
                paddingLeft: "35px",
              }}
            />
            <FaSearch
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                fontSize: "0.9rem",
              }}
            />
            {searchTerm && (
              <Button
                icon="pi pi-times"
                onClick={handleClearSearch}
                className="p-button-text"
                style={{
                  position: "absolute",
                  right: "5px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "30px",
                  height: "30px",
                  padding: 0,
                  color: "#94a3b8",
                }}
              />
            )}
          </div>

          {/* INDICADOR DE RESULTADOS */}
          <div
            style={{
              marginTop: "8px",
              fontSize: "0.7rem",
              color: "#94a3b8",
              textAlign: "right",
            }}
          >
            {condicoesFiltradas.length} condição(ões) encontrada(s)
          </div>
        </div>

        {/* LISTA DE CONDIÇÕES - GRID RESPONSIVO */}
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: getGridColumns(),
              gap: "10px",
              minHeight: "200px",
            }}
          >
            {condicoesFiltradas.length > 0 ? (
              condicoesFiltradas.map((condicao, index) => {
                const isSelected =
                  condicaoSelecionada?.codigo === condicao.codigo;
                return (
                  <div
                    key={`${condicao.codigo}-${index}`}
                    onClick={() => !isSelected && handleSelect(condicao)}
                    style={{
                      backgroundColor: isSelected ? "#fff0f0" : "white",
                      border: isSelected
                        ? "2px solid #B83732"
                        : "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px 8px",
                      cursor: isSelected ? "default" : "pointer",
                      transition: "all 0.2s ease",
                      textAlign: "center",
                      opacity: isSelected ? 0.8 : 1,
                      position: "relative",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0,0,0,0.1)";
                        e.currentTarget.style.borderColor = "#B83732";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.borderColor = "#e2e8f0";
                      }
                    }}
                    title={condicao.descricao}
                  >
                    {isSelected && (
                      <div
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "8px",
                          color: "#B83732",
                          fontSize: "0.7rem",
                        }}
                      >
                        ✓
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: isSelected ? "#B83732" : "#2d3748",
                        marginBottom: "6px",
                      }}
                    >
                      {condicao.codigo}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: isSelected ? "#B83732" : "#4a5568",
                        fontWeight: "500",
                        lineHeight: "1.3",
                      }}
                    >
                      {condicao.descricao}
                    </div>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "40px",
                  color: "#94a3b8",
                }}
              >
                <i
                  className="pi pi-inbox"
                  style={{
                    fontSize: "2rem",
                    display: "block",
                    marginBottom: "10px",
                    color: "#cbd5e1",
                  }}
                />
                <p style={{ margin: 0, fontSize: "0.9rem" }}>
                  Nenhuma condição encontrada
                </p>
                <p
                  style={{
                    margin: "5px 0 0",
                    fontSize: "0.8rem",
                    color: "#94a3b8",
                  }}
                >
                  Tente buscar por outro termo
                </p>
              </div>
            )}
          </div>
        </div>

        {/* BOTÕES DE AÇÃO */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Button
            label="FECHAR"
            icon="pi pi-times"
            onClick={handleClose}
            className="p-button-outlined"
            style={{
              padding: "10px 28px",
              minWidth: "120px",
              borderColor: "#e2e8f0",
              color: "#718096",
              fontWeight: "600",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CondicaoSelector;
